import { useState } from 'react'
import styled from 'styled-components'

import { REPORT_TEMPLATE } from '../content/tableTemplates'
import { useMessage } from '../hooks/useMessage'
import { GateFeedColumnKey } from '../models/client/observation'
import { SPACING } from '../styles/mixins/constants'
import { text } from '../styles/mixins/text'
import { BaseError } from '../util/errors/BaseError'
import { isEqual } from '../util/isEqual'

import { Check } from './Check'
import { CollapseContentContainer } from './ContentContainer'
import { PanelContainer } from './PanelContainer'
import { Spacer } from './Spacer'

type Props = {
    onClose: () => void
    onSave: (nextTemplate: Array<GateFeedColumnKey>) => Promise<void>
    columns: Array<GateFeedColumnKey>
    isLoading?: boolean
    title: string
    toastMessageOnUpdate: string
}

const OPTIONS = REPORT_TEMPLATE

export const PanelGateFeedEdit = (props: Props): JSX.Element => {
    const { columns, isLoading, onClose, onSave, title, toastMessageOnUpdate } = props
    const [selectedColumnKeys, setSelectedColumns] = useState(columns)
    const [visibleSections, setVisibleSections] = useState<Set<'columns'>>(new Set())
    const messenger = useMessage()
    const handleColumnChange = (toggledKey: GateFeedColumnKey): void => {
        let nextKeys = [...selectedColumnKeys]
        const indexOfKeys = nextKeys.findIndex((key) => key === toggledKey)
        if (indexOfKeys === -1) {
            const sortedKeys: GateFeedColumnKey[] = []
            OPTIONS.forEach(({ columns: columnData }) => {
                columnData.forEach(({ key }) => {
                    if (key === toggledKey || nextKeys.includes(key)) {
                        sortedKeys.push(key)
                    }
                })
            })
            nextKeys = [...sortedKeys]
        } else {
            nextKeys.splice(indexOfKeys, 1)
        }
        setSelectedColumns(nextKeys)
    }

    const handleSave = async (): Promise<void> => {
        try {
            await onSave(selectedColumnKeys)
            messenger.success(toastMessageOnUpdate)
            onClose()
        } catch (e) {
            if (e instanceof Error) messenger.error(e)
            else messenger.error(new BaseError({ message: 'Unknown Error' }))
        }
    }

    const hasChanges = !isEqual(columns, Array.from(selectedColumnKeys.values()))
    return (
        <PanelContainer
            header={title}
            onClose={onClose}
            onSave={handleSave}
            disableSave={!hasChanges}
            isSaving={!!isLoading}
        >
            <Scroll>
                <CollapseContentContainer
                    onToggle={() => {
                        const nextSections = new Set(Array.from(visibleSections.values()))
                        if (nextSections.has('columns')) nextSections.delete('columns')
                        else nextSections.add('columns')
                        setVisibleSections(nextSections)
                    }}
                    isOpen={visibleSections.has('columns')}
                    title="Columns"
                >
                    <Row>
                        {OPTIONS.map((section) => (
                            <ColumnContainer key={section.key}>
                                <Row>
                                    <SectionLabel>
                                        {section.key === 'ASSET' ? 'Event' : section.label}
                                    </SectionLabel>
                                </Row>
                                <Spacer vertical size={2} />
                                {section.columns.map((column) => {
                                    if (column.key === 'EDIT_OPTIONS')
                                        return <div key={column.key} />
                                    return (
                                        <DataElement key={column.key}>
                                            <Check
                                                isChecked={selectedColumnKeys.includes(column.key)}
                                                onChange={() => handleColumnChange(column.key)}
                                                label={column.label}
                                            />
                                        </DataElement>
                                    )
                                })}
                            </ColumnContainer>
                        ))}
                    </Row>
                </CollapseContentContainer>
            </Scroll>
        </PanelContainer>
    )
}

const SectionLabel = styled.span`
    ${text('label')}
`

const Scroll = styled.div`
    overflow: auto;
    flex: 1 1 auto;
    min-height: 0;
    min-width: 0;
`

const Row = styled.div`
    display: flex;
    flex-flow: row wrap;
    align-items: flex-start;
    flex: 1 1 auto;
    min-height: 0;
`

const ColumnContainer = styled.div`
    display: flex;
    flex-flow: column nowrap;
    flex: 1 1 auto;
    max-height: 100%;
    min-height: 0;
    width: 50%;
    align-items: flex-start;
    flex: 1 0 auto;
    padding-right: ${SPACING[4]};
    padding-left: ${SPACING[4]};
    &:nth-child(even) {
        padding-right: 0px;
    }
    &:nth-child(odd) {
        padding-left: 0px;
    }
`

const DataElement = styled(Row)`
    width: 100%;
    ${text('sub', 'secondary')}
    flex-wrap: nowrap;
    flex: 1 0 auto;
    min-width: 0;
    margin-bottom: ${SPACING[2]};
    .ant-checkbox-wrapper {
        position: relative;
        width: 16px;
        height: 16px;
    }
    .ant-checkbox {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
    }
`
